import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Loader2, Plus, Eye, EyeOff, Trash2, Pencil, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import {
  fetchPerkCategories,
  adminCreatePerkCategory,
  adminUpdatePerkCategory,
  adminDeletePerkCategory,
} from "../../lib/studioApi";

/**
 * PerkCategoriesDialog — admin sub-view to create / rename / hide / delete
 * perk categories. Renamed/created categories propagate to the upload dialog
 * via the onChanged callback so the caller can refresh its category list.
 */
export default function PerkCategoriesDialog({ open, onOpenChange, onChanged }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const load = () => {
    setLoading(true);
    fetchPerkCategories({ asAdmin: true })
      .then(setCats)
      .catch(() => toast.error("Failed to load categories"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await adminCreatePerkCategory({ name: newName.trim() });
      setNewName("");
      load();
      onChanged?.();
      toast.success("Category added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add category");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditingName(cat.name);
  };

  const saveEdit = async () => {
    if (!editingName.trim() || !editingId) {
      setEditingId(null);
      return;
    }
    try {
      await adminUpdatePerkCategory(editingId, { name: editingName.trim() });
      setEditingId(null);
      setEditingName("");
      load();
      onChanged?.();
      toast.success("Category updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const toggleHidden = async (cat) => {
    try {
      await adminUpdatePerkCategory(cat.id, { hidden: !cat.hidden });
      load();
      onChanged?.();
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"?`)) return;
    try {
      await adminDeletePerkCategory(cat.id);
      load();
      onChanged?.();
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="perk-categories-dialog"
        className="max-w-md bg-[#10162a] border-white/10 text-white"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-white">
            Manage categories
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Add new */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Add category
            </Label>
            <div className="mt-1 flex gap-2">
              <Input
                data-testid="perk-new-category-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="e.g. Templates"
                className="bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
              />
              <Button
                type="button"
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                data-testid="perk-new-category-btn"
                className="bg-paul-yellow hover:brightness-105 text-navy font-bold"
                style={{ color: "#0b1535" }}
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="rounded-lg border border-white/10 overflow-hidden">
            {loading ? (
              <div className="p-6 text-center text-white/40">
                <Loader2 className="w-5 h-5 animate-spin inline-block" />
              </div>
            ) : cats.length === 0 ? (
              <div className="p-6 text-center text-white/40 text-sm">No categories yet.</div>
            ) : (
              <ul>
                {cats.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`perk-cat-row-${c.id}`}
                    className="px-3 py-2.5 border-b border-white/5 last:border-0 flex items-center gap-2"
                  >
                    {editingId === c.id ? (
                      <>
                        <Input
                          autoFocus
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          className="h-8 bg-[#0b0f1e] border-white/10 text-white"
                        />
                        <button
                          type="button"
                          onClick={saveEdit}
                          className="text-paul-yellow hover:text-paul-yellow/80"
                          aria-label="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="text-white/40 hover:text-white"
                          aria-label="Cancel"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          className={`flex-1 text-sm font-medium ${
                            c.hidden ? "text-white/40 line-through" : "text-white"
                          }`}
                        >
                          {c.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => startEdit(c)}
                          className="text-white/50 hover:text-paul-yellow p-1"
                          title="Rename"
                          data-testid={`perk-cat-rename-${c.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleHidden(c)}
                          className="text-white/50 hover:text-paul-yellow p-1"
                          title={c.hidden ? "Show" : "Hide"}
                          data-testid={`perk-cat-toggle-${c.id}`}
                        >
                          {c.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(c)}
                          className="text-red-400/70 hover:text-red-400 p-1"
                          title="Delete"
                          data-testid={`perk-cat-delete-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
